import axios from 'axios';

const ROOT = `${process.env.REACT_APP_BACKEND_URL}/api/admin/limits`;
const TENANTS = `${process.env.REACT_APP_BACKEND_URL}/api/admin/tenants`;

const config = (token, extra = {}) => ({
  ...extra,
  headers: { ...(extra.headers || {}), Authorization: `Bearer ${token}` },
});

/**
 * Thin transport only. The server is the quota authority: this module never
 * resolves precedence, applies defaults or invents usage. Keeping the contract
 * in one file also makes a backend route rename a one-file change.
 */
export const limitsApi = {
  async listTemplates(token) {
    const data = (await axios.get(`${ROOT}/templates`, config(token))).data;
    const definitions = Array.isArray(data) ? data : data.templates || data.items || [];
    // The API separates a logical template from its immutable versions. The UI
    // works on versions, so flatten here rather than leaking that transport
    // detail into every component.
    if (definitions.some((row) => row.version_id || row.version != null || row.version_number != null)) {
      return { ...data, templates: definitions };
    }
    const versions = await Promise.all(definitions.map(async (definition) => {
      const id = definition.id || definition.template_id;
      const response = await axios.get(`${ROOT}/templates/${id}/versions`, config(token));
      const rows = Array.isArray(response.data) ? response.data : response.data.versions || response.data.items || [];
      return rows.map((row) => ({ ...row, template_id: row.template_id || id, name: row.name || definition.name }));
    }));
    return { ...data, templates: versions.flat() };
  },
  async getCatalog(token) {
    return (await axios.get(`${ROOT}/catalog`, config(token))).data;
  },
  async createDraft(token, payload) {
    return (await axios.post(`${ROOT}/templates`, payload, config(token))).data;
  },
  async updateDraft(token, versionId, payload) {
    return (await axios.patch(`${ROOT}/templates/${versionId}`, payload, config(token))).data;
  },
  async publishTemplate(token, versionId, payload) {
    return (await axios.post(`${ROOT}/templates/${versionId}/publish`, payload, config(token))).data;
  },
  async cloneTemplate(token, versionId, payload) {
    return (await axios.post(`${ROOT}/templates/${versionId}/clone`, payload, config(token))).data;
  },
  async setDefaultTemplate(token, versionId, payload) {
    return (await axios.post(`${ROOT}/templates/${versionId}/make-default`, payload, config(token))).data;
  },
  async archiveTemplate(token, versionId, payload) {
    return (await axios.post(`${ROOT}/templates/${versionId}/archive`, payload, config(token))).data;
  },
  async compareTemplates(token, left, right) {
    return (await axios.get(`${ROOT}/templates/compare`, config(token, { params: { left, right } }))).data;
  },
  async getTenantLimits(token, tenantId, { scope = null, scopeId = null } = {}) {
    const params = {};
    if (scope && scopeId) { params.scope = scope; params.scope_id = scopeId; }
    return (await axios.get(
      `${TENANTS}/${tenantId}/limits/effective`, config(token, { params }),
    )).data;
  },
  async setDocFlowStorageExtra(token, tenantId, payload) {
    return (await axios.put(
      `${TENANTS}/${tenantId}/limits/docflow-storage-extra`, payload, config(token),
    )).data;
  },
  async previewTenantLimit(token, tenantId, payload) {
    return (await axios.post(
      `${TENANTS}/${tenantId}/limits/overrides/impact`, payload, config(token),
    )).data;
  },
  async saveTenantOverride(token, tenantId, quotaKey, payload) {
    return (await axios.put(
      `${TENANTS}/${tenantId}/limits/overrides/${encodeURIComponent(quotaKey)}`, payload, config(token),
    )).data;
  },
  async restoreTenantLimit(token, tenantId, quotaKey, payload) {
    return (await axios.delete(
      `${TENANTS}/${tenantId}/limits/overrides/${encodeURIComponent(quotaKey)}`, config(token, { data: payload }),
    )).data;
  },
  async getTenantLimitHistory(token, tenantId, {
    quotaKey = null, cursor = null, limit = 50, scope = null, scopeId = null,
  } = {}) {
    const params = { limit };
    if (quotaKey) params.quota_key = quotaKey;
    if (cursor) params.cursor = cursor;
    if (scope && scopeId) { params.scope = scope; params.scope_id = scopeId; }
    return (await axios.get(
      `${TENANTS}/${tenantId}/limits/history`, config(token, { params }),
    )).data;
  },
  async previewTemplateMigration(token, tenantId, payload) {
    return (await axios.post(
      `${TENANTS}/${tenantId}/limits/template-migration/preview`, payload, config(token),
    )).data;
  },
  async applyTemplateMigration(token, tenantId, payload) {
    return (await axios.post(
      `${TENANTS}/${tenantId}/limits/template-migration/apply`, payload, config(token),
    )).data;
  },
};

export default limitsApi;
